<template>
  <el-dialog
    v-model="visible"
    width="500px"
    @close="onClose"
    :show-close="false"
    align-center
    append-to-body
    destroy-on-close
    class="appointment-modal appointment-modal--view">
    <div class="appointment-modal-body">
    <button class="modal-close-btn" @click="onClose">×</button>
    <div class="appointment-header-visual">
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>{{ formatDate(appointment.date) }} ({{ formattedDate(appointment.date) }} {{ weekdayLabel }})</span>
        </div>
        <div class="header-item">
          <el-icon>
            <Clock />
          </el-icon>
          <span>{{ appointment.start_time }} – {{ appointment.end_time }}</span>
        </div>
      </div>
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>{{ staffLabel }}</span>
        </div>
        <div class="header-item">
          <img src="/img/cabinet-modal.svg" class="cabinet-header-icon" alt="" aria-hidden="true" />
          <span>{{ appointment.room_name }}</span>
        </div>
      </div>
      <div class="header-row status-row">
        <div class="header-item">
          <el-icon>
            <AlarmClock />
          </el-icon>
          <span>{{ appointment.duration_time || totalDuration }} мин</span>
        </div>
      </div>
    </div>

    <div v-if="!clientIsDeleted" class="client-info-card">
      <div class="client-phone" v-if="appointment.client_phone">
        <el-icon>
          <Phone />
        </el-icon>
        <a :href="`tel:${appointment.client_phone}`" class="phone-link">{{ appointment.client_phone }}</a>
        <el-tooltip content="Скопировать" placement="top" popper-class="calendar-copy-tooltip">
          <el-button :icon="CopyDocument" text @click="copyPhone" class="copy-button" circle />
        </el-tooltip>
      </div>
      <div class="client-name" @click="showClientProfile" title="Показать профиль клиента">
        <el-icon>
          <UserFilled />
        </el-icon>
        <span class="name-text">{{ appointment.client_name }}</span>
        <el-button :icon="Edit" text circle @click.stop="showClientProfile" class="edit-button"
                   title="Редактировать клиента" />
      </div>
    </div>
    <div v-else class="client-info-card client-deleted">
      <el-icon>
        <UserFilled />
      </el-icon>
      <span style="color:#aaa;font-size:15px;margin-left:4px;">Клиент удалён или не найден</span>
    </div>

    <el-table
      v-if="appointment.services.length"
      class="services-table"
      :data="appointment.services"
      size="small"
      show-overflow-tooltip
    >
      <el-table-column prop="name" label="Услуга" />
      <el-table-column prop="duration" label="Минуты" width="110" />
      <el-table-column prop="price" label="Сумма" width="130" />
    </el-table>
    <div v-if="appointment.services.length" class="services-total">
      <span>Общая сумма:</span>
      <b>{{ appointment.total_sum }} Р</b>
    </div>

    <div class="appointment-view-content">
      <div class="appointment-view-row v-comment-row" v-if="appointment.comment">
        <img src="/img/appointment-comment-icon.svg" class="comment-modal-icon" alt="" aria-hidden="true" />
        <div class="comment-box">
          <span class="comment-label">Комментарий:&nbsp;</span>
          <span class="comment-value">{{ appointment.comment }}</span>
        </div>
      </div>
    </div>
    </div>
    <template #footer>
      <div class="footer-bar">
        <div class="footer-status">
          <el-dropdown @command="changeStatus" trigger="click" popper-class="appointment-status-dropdown" v-if="hasPermission('appointments.edit')">
            <span class="status-trigger" :style="{ color: STATUS_MAP[appointment.status]?.color }">
              <img src="/img/appointment-status-icon.svg" class="status-icon-img" alt="" aria-hidden="true" />
              <span class="status-label">{{ STATUS_MAP[appointment.status]?.label || appointment.status }}</span>
              <el-icon class="status-arrow"><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="(obj, key) in STATUS_MAP"
                  :key="key"
                  :command="key"
                  :disabled="appointment.status === key"
                  :style="{ color: obj.color }"
                >
                  {{ obj.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <div class="footer-actions">
          <template v-if="appointment.status === 'done'">
            <el-button type="primary" class="footer-main-btn" @click="cancelCompletion">Отменить выполнение</el-button>
          </template>
          <template v-else>
            <el-button
              v-if="hasPermission('appointments.edit')"
              class="footer-icon-btn footer-icon-btn--edit"
              circle
              @click="onEdit"
              title="Редактировать">
              <img src="/img/appointment-edit-action.svg" class="footer-action-icon" alt="" aria-hidden="true" />
            </el-button>
            <el-button
              v-if="hasPermission('appointments.delete')"
              class="footer-icon-btn footer-icon-btn--delete"
              circle
              @click="onDelete"
              title="Удалить">
              <img src="/img/appointment-delete-action.svg" class="footer-action-icon" alt="" aria-hidden="true" />
            </el-button>
            <el-button
              v-if="hasPermission('appointments.complete') && !['no_show', 'done', 'canceled'].includes(appointment.status)"
              type="primary"
              class="footer-main-btn"
              @click="openClosed">
              Обновить
            </el-button>
          </template>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import {
  AlarmClock,
  ArrowDown,
  Calendar,
  Clock,
  Files,
  Edit,
  Phone,
  UserFilled
} from '@element-plus/icons-vue'
import { mapState } from 'vuex'
import { hasPermission } from '@/components/util/accessHelper'

export default {
  name: 'AppointmentViewModal',
  components: {
    AlarmClock,
    Clock,
    Calendar,
    UserFilled,
    Phone,
    ArrowDown
  },
  props: {
    date: { type: String, required: true },
    appointment: { type: Object, required: true }
  },
  data() {
    return {
      showCompleteModal: false,
      visible: true,
      STATUS_MAP: {
        planned: { label: 'Запланировано', color: '#22c0ff', icon: ['fas', 'clock'] },
        confirmed: { label: 'Подтверждено', color: '#22c0ff', icon: ['fas', 'circle-check'] },
        no_show: { label: 'Не пришёл', color: '#ff7d7d', icon: ['fas', 'ban'] },
        canceled: { label: 'Отменено', color: '#8ca0c5', icon: ['fas', 'trash-can'] }
      }
    }
  },
  computed: {
    ...mapState('custom_fields', ['fields']),
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    CopyDocument() {
      return Files
    },
    Edit() {
      return Edit
    },
    clientIsDeleted() {
      // Если нет имени и нет телефона — считаем "удалён"
      const name = (this.appointment.client_name || '').trim()
      const phone = (this.appointment.client_phone || '').trim()
      // id может быть, но если имя и телефон пусты — это "удалён"
      return !name && !phone
    },
    totalDuration() {
      // если нет appointment.duration_time, считаем по услугам
      if (this.appointment && this.appointment.services && this.appointment.services.length) {
        return this.appointment.services.reduce((sum, s) => sum + (s.duration || 0), 0)
      }
      return 0
    },
    staffLabel() {
      return this.appointment.user_email || this.appointment.employee_email || this.appointment.user_name || ''
    }
  },
  methods: {
    hasPermission,
    formattedDate(date) {
      const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
      ]
      // YYYY-MM-DD
      const [, month, day] = date.split('-')
      const dayStr = day.length === 1 ? `0${day}` : day
      const monthName = months[parseInt(month, 10) - 1]
      return `${dayStr} ${monthName}`
    },
    formatDate(date) {
      if (!date) return ''
      // формат "2025-06-06" → "06.06.2025"
      const parts = date.split('-')
      if (parts.length !== 3) return date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    onClose() {
      this.visible = false
      this.$emit('close')
    },
    onEdit() {
      this.$emit('edit', this.appointment)
    },
    showClientProfile() {
      if (this.appointment.client_id) {
        this.$emit('show-client', this.appointment.client_id)
      }
    },
    copyPhone() {
      if (this.appointment.client_phone) {
        navigator.clipboard.writeText(this.appointment.client_phone).then(() => {
          this.$message.success('Скопировано')
        }).catch(() => {
          alert('Не удалось скопировать')
        })
      }
    },
    changeStatus(newStatus) {
      this.$confirm(`Вы уверены, что хотите сменить статус на "${this.STATUS_MAP[newStatus].label}"?`, 'Смена статуса', {
        confirmButtonText: 'Да',
        cancelButtonText: 'Отмена',
        type: 'warning'
      }).then(() => {
        // Здесь вызов API на обновление статуса
        // this.updateStatus(newStatus)
        const data = {
          id: this.appointment.id,
          status: newStatus,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        };
        this.$store.dispatch('appointments/statusAppointment', data);
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = newStatus;
        this.$message.success('Статус обновлен')
      }).catch(() => {
        // Просто игнорируем отказ — пользователь нажал "Отмена"
      })
    },
    openClosed() {
      this.$emit('close')           // Закрыть предпросмотр
      this.$emit('open-complete')   // Сообщить родителю открыть "Выполнить"
    },
    async cancelCompletion() {
      try {
        await this.$confirm('Отменить выполнение и вернуть запись в активные?', 'Подтверждение', {
          confirmButtonText: 'Да',
          cancelButtonText: 'Отмена',
          type: 'warning'
        })
        await this.$store.dispatch('appointments/cancelCompletion', {
          appointment_id: this.appointment.id,
          account_id: this.appointment.account_id,
          date: this.date,
          branch_id: this.appointment.branch_id,
          city_id: this.appointment.city_id,
        })
        // --- Добавлено для реактивного обновления ---
        // eslint-disable-next-line vue/no-mutating-props
        this.appointment.status = 'planned'
        this.$emit('refresh-appointments')
        this.$message.success('Выполнение отменено, средства возвращены')
      } catch (err) {
        // отмена пользователем
      }
    },
    onDelete() {
      this.$confirm('Вы уверены, что хотите удалить эту запись?', 'Подтверждение удаления', {
        confirmButtonText: 'Удалить',
        cancelButtonText: 'Отмена',
        type: 'warning',
        customClass: 'appointment-delete-confirm'
      }).then(() => {
        // Отправляем запрос в store
        this.$store.dispatch('appointments/deleteAppointment', {
          id: this.appointment.id,
          date: this.appointment.date,
          city_id: this.appointment.city_id,
          branch_id: this.appointment.branch_id
        })
          .then(() => {
            this.$message.success('Запись удалена')
            this.$emit('deleted', this.appointment.id)
            this.onClose()
          })
          .catch(() => {
            this.$message.error('Ошибка при удалении записи')
          })
      }).catch(() => {
        // Пользователь отменил удаление
      })
    },
  }
}
</script>

<style scoped>
.phone-link {
  color: inherit;
  text-decoration: none;
}

.phone-link:hover {
  text-decoration: underline;
}

.client-name {
  cursor: pointer;
  user-select: none;
}

.name-text {
  user-select: text;
}

.client-deleted {
  color: #9cb3da;
  justify-content: flex-start;
  align-items: center;
  min-height: 34px;
  font-style: italic;
}

.appointment-view-content {
  padding-top: 10px;
}

.appointment-view-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.footer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
}

.footer-status {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  min-height: 34px;
}

.status-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  max-width: 100%;
}

.status-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-icon,
.status-arrow {
  flex: 0 0 auto;
}

.footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
}

.footer-actions :deep(.el-button) {
  margin: 0 !important;
}

.footer-actions :deep(.el-button + .el-button) {
  margin-left: 0 !important;
}

@media (max-width: 640px) {
  .footer-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-actions {
    justify-content: flex-start;
  }
}
</style>
